// ---------------------------------------------------------------------------
// Test Mode — task reports (the 7 endpoints under /api/reports/tasks).
//
// Shaped exactly like lib/taskReportsApi.ts, and derived from the same task
// list every other page shows, so the numbers here agree with the boards, the
// calendar and the dashboard instead of being a second, drifting dataset.
// ---------------------------------------------------------------------------

import type {
  CycleTimeReport,
  HoursReport,
  HoursReportTask,
  OverdueBucket,
  OverdueReport,
  OverdueTaskRow,
  OverviewReport,
  OverrunsReport,
  ThroughputBucket,
  ThroughputReport,
  WorkloadReport,
} from '../lib/taskReportsApi';
import type { Task } from '../lib/tasksApi';
import { isoDate } from './company';
import { buildPeople } from './people';
import { PROJECT_SEEDS } from './projects';
import { buildTasks } from './tasks';

function isClosed(t: Task): boolean {
  return t.statusCategory === 'COMPLETED' || t.statusCategory === 'CANCELLED';
}

/** Deterministic "hours actually logged" for a task: most land near the
 *  estimate, some under, a stable minority over. */
function spentFor(t: Task): number {
  const est = t.estimate ?? 4;
  const n = Number(t.id.replace(/\D/g, '')) || 1;
  const factor = [0.7, 0.85, 1, 1.1, 0.9, 1.35, 0.6, 1.6][n % 8]!;
  const progress =
    t.statusCategory === 'COMPLETED' ? 1 : t.statusCategory === 'STARTED' ? 0.55 : 0.1;
  return Math.round(est * factor * progress * 10) / 10;
}

function projectRef(t: Task) {
  const p = PROJECT_SEEDS.find((s) => s.id === t.projectId);
  return p ? { id: p.id, name: p.name, code: p.code } : null;
}

function toHoursTask(t: Task): HoursReportTask {
  const spent = spentFor(t);
  const estimate = t.estimate ?? null;
  const delta = estimate === null ? null : Math.round((spent - estimate) * 10) / 10;
  return {
    id: t.id,
    identifier: t.identifier,
    title: t.title,
    project: projectRef(t),
    assignee: t.assignee,
    status: {
      slug: t.status,
      name: t.statusLabel,
      category: t.statusCategory,
      color: t.statusColor,
    },
    estimate,
    spent,
    delta,
    pct: estimate ? Math.round((spent / estimate) * 100) / 100 : null,
    completedAt: t.statusCategory === 'COMPLETED' ? t.updatedAt : null,
  };
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000);
}

export function buildReportOverview(): OverviewReport {
  const tasks = buildTasks();
  const completed = tasks.filter((t) => t.statusCategory === 'COMPLETED');
  const spentHours = tasks.reduce((s, t) => s + spentFor(t), 0);
  const estimatedHours = tasks.reduce((s, t) => s + (t.estimate ?? 0), 0);
  const completedEstimated = completed.filter((t) => t.estimate);
  const estimateAccuracy =
    completedEstimated.length > 0
      ? completedEstimated.reduce((s, t) => s + (t.estimate ?? 0), 0) /
        Math.max(
          completedEstimated.reduce((s, t) => s + spentFor(t), 0),
          1,
        )
      : null;
  const onTime = completed.filter((t) => !t.dueDate || t.updatedAt.slice(0, 10) <= t.dueDate);
  const overrun = tasks.filter((t) => t.estimate && spentFor(t) > t.estimate);
  return {
    spentHours: Math.round(spentHours),
    estimatedHours,
    estimateAccuracy: estimateAccuracy === null ? null : Math.round(estimateAccuracy * 100) / 100,
    createdCount: tasks.length,
    completedCount: completed.length,
    onTimeCount: onTime.length,
    onTimeRate: completed.length
      ? Math.round((onTime.length / completed.length) * 100) / 100
      : null,
    overrunTaskCount: overrun.length,
    activePeople: new Set(tasks.map((t) => t.assigneeId).filter(Boolean)).size,
  };
}

export function buildReportHours(): HoursReport {
  const tasks = buildTasks();
  const byProject = PROJECT_SEEDS.map((p) => {
    const own = tasks.filter((t) => t.projectId === p.id);
    return {
      projectId: p.id,
      name: p.name,
      code: p.code,
      estimatedHours: own.reduce((s, t) => s + (t.estimate ?? 0), 0),
      spentHours: Math.round(own.reduce((s, t) => s + spentFor(t), 0)),
    };
  }).filter((row) => row.estimatedHours > 0);
  const byPersonMap = new Map<string, { name: string; spentHours: number }>();
  for (const t of tasks) {
    if (!t.assignee) continue;
    const row = byPersonMap.get(t.assignee.id) ?? { name: t.assignee.name, spentHours: 0 };
    row.spentHours += spentFor(t);
    byPersonMap.set(t.assignee.id, row);
  }
  const byPerson = [...byPersonMap.entries()]
    .map(([userId, row]) => ({ userId, name: row.name, spentHours: Math.round(row.spentHours) }))
    .sort((a, b) => b.spentHours - a.spentHours);
  return {
    byProject,
    byPerson,
    tasks: tasks
      .filter((t) => spentFor(t) > 0)
      .map(toHoursTask)
      .sort((a, b) => b.spent - a.spent)
      .slice(0, 60),
  };
}

export function buildReportOverruns(): OverrunsReport {
  const rows = buildTasks()
    .filter((t) => t.estimate && spentFor(t) > t.estimate)
    .map(toHoursTask)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0));
  const byPersonMap = new Map<string, { name: string | null; taskCount: number; hours: number }>();
  for (const r of rows) {
    const key = r.assignee?.id ?? 'none';
    const row = byPersonMap.get(key) ?? { name: r.assignee?.name ?? null, taskCount: 0, hours: 0 };
    row.taskCount += 1;
    row.hours += r.delta ?? 0;
    byPersonMap.set(key, row);
  }
  return {
    byPerson: [...byPersonMap.entries()]
      .map(([key, row]) => ({
        userId: key === 'none' ? null : key,
        name: row.name,
        taskCount: row.taskCount,
        totalOverrunHours: Math.round(row.hours * 10) / 10,
      }))
      .sort((a, b) => b.totalOverrunHours - a.totalOverrunHours),
    tasks: rows,
  };
}

export function buildReportThroughput(bucket: ThroughputBucket = 'month'): ThroughputReport {
  const buckets: ThroughputReport['buckets'] = [];
  const now = new Date();
  if (bucket === 'week') {
    // Twelve weeks back, Monday-aligned.
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    for (let i = 11; i >= 0; i--) {
      const d = new Date(monday);
      d.setDate(monday.getDate() - i * 7);
      const base = 6 + ((i * 5) % 7);
      buckets.push({
        bucketStart: d.toISOString().slice(0, 10),
        created: base + (i % 3),
        completed: base - 1 + ((i * 2) % 3),
      });
    }
  } else {
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const base = 22 + ((i * 7) % 11);
      buckets.push({
        bucketStart: d.toISOString().slice(0, 10),
        created: base + 3,
        completed: base - (i % 4),
      });
    }
  }
  return { bucket, buckets };
}

export function buildReportCycleTime(): CycleTimeReport {
  const tasks = buildTasks();
  const byProject = PROJECT_SEEDS.map((p, i) => {
    const done = tasks.filter((t) => t.projectId === p.id && t.statusCategory === 'COMPLETED');
    if (done.length === 0) return null;
    const avgCycle = 4 + (i % 6) * 1.5;
    return {
      projectId: p.id,
      name: p.name,
      code: p.code,
      count: done.length,
      avgCycleDays: avgCycle,
      medianCycleDays: Math.round((avgCycle - 1) * 10) / 10,
      avgLeadDays: avgCycle + 6 + (i % 3),
      medianLeadDays: avgCycle + 4 + (i % 3),
    };
  }).filter((row): row is NonNullable<typeof row> => row !== null);
  const count = byProject.reduce((s, r) => s + r.count, 0);
  const avg = (key: 'avgCycleDays' | 'medianCycleDays' | 'avgLeadDays' | 'medianLeadDays') =>
    count
      ? Math.round((byProject.reduce((s, r) => s + (r[key] ?? 0) * r.count, 0) / count) * 10) / 10
      : null;
  return {
    count,
    avgCycleDays: avg('avgCycleDays'),
    medianCycleDays: avg('medianCycleDays'),
    avgLeadDays: avg('avgLeadDays'),
    medianLeadDays: avg('medianLeadDays'),
    byProject,
  };
}

export function buildReportWorkload(): WorkloadReport {
  const tasks = buildTasks();
  return {
    people: buildPeople()
      .filter((p) => p.isActive)
      .map((p) => {
        const open = tasks.filter((t) => t.assigneeId === p.id && !isClosed(t));
        const openEstimatedHours = open.reduce((s, t) => s + (t.estimate ?? 4), 0);
        const remainingHours = Math.max(
          0,
          Math.round(open.reduce((s, t) => s + Math.max(0, (t.estimate ?? 4) - spentFor(t)), 0)),
        );
        return {
          userId: p.id,
          name: p.fullName,
          capacity: p.capacity,
          openTasks: open.length,
          openEstimatedHours,
          remainingHours,
          utilization: p.capacity ? Math.round((remainingHours / p.capacity) * 100) / 100 : null,
        };
      })
      .filter((row) => row.openTasks > 0)
      .sort((a, b) => b.remainingHours - a.remainingHours),
  };
}

export function buildReportOverdue(): OverdueReport {
  const today = isoDate(0);
  const rows: OverdueTaskRow[] = buildTasks()
    .filter((t) => t.dueDate && t.dueDate < today && !isClosed(t))
    .map((t) => {
      const daysOverdue = daysBetween(today, t.dueDate!);
      const bucket: OverdueBucket =
        daysOverdue <= 7 ? 'd1_7' : daysOverdue <= 30 ? 'd7_30' : 'd30plus';
      return {
        id: t.id,
        identifier: t.identifier,
        title: t.title,
        project: projectRef(t),
        assignee: t.assignee,
        status: {
          slug: t.status,
          name: t.statusLabel,
          category: t.statusCategory,
          color: t.statusColor,
        },
        estimate: t.estimate ?? null,
        dueDate: t.dueDate!,
        daysOverdue,
        bucket,
      };
    })
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  const buckets: Record<OverdueBucket, number> = { d1_7: 0, d7_30: 0, d30plus: 0 };
  const byPersonMap = new Map<string, { name: string | null; count: number }>();
  const byProjectMap = new Map<string, { name: string; code: string | null; count: number }>();
  for (const r of rows) {
    buckets[r.bucket] += 1;
    const pk = r.assignee?.id ?? 'none';
    const pr = byPersonMap.get(pk) ?? { name: r.assignee?.name ?? null, count: 0 };
    pr.count += 1;
    byPersonMap.set(pk, pr);
    if (r.project) {
      const jr = byProjectMap.get(r.project.id) ?? {
        name: r.project.name,
        code: r.project.code,
        count: 0,
      };
      jr.count += 1;
      byProjectMap.set(r.project.id, jr);
    }
  }
  return {
    buckets,
    byPerson: [...byPersonMap.entries()]
      .map(([key, row]) => ({
        userId: key === 'none' ? null : key,
        name: row.name,
        count: row.count,
      }))
      .sort((a, b) => b.count - a.count),
    byProject: [...byProjectMap.entries()]
      .map(([projectId, row]) => ({ projectId, ...row }))
      .sort((a, b) => b.count - a.count),
    tasks: rows,
  };
}
